import type { FC, ReactNode } from 'react'
import type { Product } from '@/types'

import Link from 'next/link'
import { ArrowRight, MapPin, Ruler } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ProductVisual } from '@/components/products/product-visual'

interface ProductCardProps {
    product: Product
    className?: string
}

export const ProductCard: FC<ProductCardProps> = ({ product, className }): ReactNode => {
    const href = `/products/${product.slug}`

    return (
        <article
            className={cn(
                'group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm shadow-foreground/5 transition duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-foreground/10 motion-reduce:transition-none motion-reduce:hover:translate-y-0',
                className
            )}
        >
            <div className='relative'>
                <Link
                    href={href}
                    tabIndex={-1}
                    aria-hidden='true'
                    className='block aspect-[4/3] overflow-hidden bg-muted'
                >
                    <ProductVisual
                        product={product}
                        className='transition-transform duration-500 group-hover:scale-[1.04] motion-reduce:transition-none'
                    />
                </Link>

                {product.category_name && (
                    <span
                        className='absolute top-4 left-4 rounded-full bg-background/90 px-3 py-1 text-xs font-bold text-foreground shadow-sm backdrop-blur'
                        data-mdlfy-cms={`categories:${product.category_id}:name`}
                    >
                        {product.category_name}
                    </span>
                )}
            </div>

            <div className='flex flex-1 flex-col p-6'>
                <h3 className='text-lg leading-snug font-bold text-foreground'>
                    <Link
                        href={href}
                        className='rounded-sm transition-colors hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
                    >
                        <span data-mdlfy-cms={`products:${product.id}:name`}>
                            {product.name}
                        </span>
                    </Link>
                </h3>

                {product.short_description && (
                    <p
                        className='mt-2 line-clamp-3 text-sm leading-relaxed text-muted-foreground'
                        data-mdlfy-cms={`products:${product.id}:short_description`}
                    >
                        {product.short_description}
                    </p>
                )}

                {(product.origin || product.grain_length) && (
                    <dl className='mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm text-foreground'>
                        {product.origin && (
                            <div className='flex items-center gap-1.5'>
                                <dt>
                                    <MapPin className='size-4 text-bronze' aria-hidden='true' />
                                    <span className='sr-only'>
                                        Origin
                                    </span>
                                </dt>

                                <dd className='font-semibold' data-mdlfy-cms={`products:${product.id}:origin`}>
                                    {product.origin}
                                </dd>
                            </div>
                        )}

                        {product.grain_length && (
                            <div className='flex items-center gap-1.5'>
                                <dt>
                                    <Ruler className='size-4 text-bronze' aria-hidden='true' />
                                    <span className='sr-only'>
                                        Average grain length
                                    </span>
                                </dt>

                                <dd className='font-semibold' data-mdlfy-cms={`products:${product.id}:grain_length`}>
                                    {product.grain_length}
                                </dd>
                            </div>
                        )}
                    </dl>
                )}

                <div className='mt-auto pt-6'>
                    <Link
                        href={href}
                        aria-label={`View specification for ${product.name}`}
                        className='inline-flex items-center gap-1.5 rounded-sm text-sm font-bold text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
                    >
                        View specification
                        <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                    </Link>
                </div>
            </div>
        </article>
    )
}
